/**
 * Does this reader have any feeds at all?
 *
 * The For You link in the navbar is gated on that one bit, and the navbar is
 * rendered by every reader page, so asking the server on each route change
 * would be a request per navigation for an answer that changes only when
 * somebody saves or deletes a feed. This is the session-lived cache behind it.
 *
 * The gate is deliberately "has any feed", not "has any feed in For You": a
 * reader who has excluded every one of their feeds still needs the link, or the
 * page that says so is unreachable.
 *
 * Pure store, no fetching -- `useSavedSearchPresence` owns the request, and any
 * page that has already listed the reader's feeds can settle the answer for
 * free by calling `setSavedSearchPresence`.
 */

type Listener = (present: boolean | null) => void;

let present: boolean | null = null;
const listeners = new Set<Listener>();

/** The cached answer, or null when nobody has established it yet. */
export function getSavedSearchPresence(): boolean | null {
  return present;
}

export function setSavedSearchPresence(next: boolean): void {
  if (present === next) return;
  present = next;
  listeners.forEach((listener) => listener(present));
}

/** Drop the cached answer so the next subscriber re-asks the server. */
export function clearSavedSearchPresence(): void {
  present = null;
  listeners.forEach((listener) => listener(present));
}

export function subscribeToSavedSearchPresence(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
